import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { calculateKtcStyleAdjustedTotals } from "../utils/dynastyTradeCalculator";
import {
  buildBucketedPickName,
  type DynastyRosterPlayer,
  type DynastyRosterTeam,
  type FindTradeRecommendation,
  type LeagueSettingsSummary,
  type PickPlayerAssignment,
  type PickValueBucket,
  type TradeAsset,
} from "../features/shared/dynastyTradingPageUtils";

export type TradeSelectionMode = {
  teamId: number;
} | null;

type UseDynastyTradeWorkspaceArgs = {
  importedTeams: DynastyRosterTeam[];
  isNarrowViewport: boolean;
  leagueSeason: number | null;
  leagueSettings: LeagueSettingsSummary | null;
  overallMaxPlayerValue: number;
  pickValueTemplates: Map<string, { "1QB": number | null; "2QB": number | null }>;
  setIsMobileFindTradeMenuOpen: Dispatch<SetStateAction<boolean>>;
  setIsTargetTeamMenuOpen: Dispatch<SetStateAction<boolean>>;
  setMobileActiveTab: Dispatch<SetStateAction<"teams" | "trade" | "find">>;
};

export function useDynastyTradeWorkspace(args: UseDynastyTradeWorkspaceArgs) {
  const {
    importedTeams,
    isNarrowViewport,
    leagueSeason,
    leagueSettings,
    overallMaxPlayerValue,
    pickValueTemplates,
    setIsMobileFindTradeMenuOpen,
    setIsTargetTeamMenuOpen,
    setMobileActiveTab,
  } = args;
  const [openRosterIds, setOpenRosterIds] = useState<number[]>([]);
  const [tradeSelections, setTradeSelections] = useState<Record<number, Record<number, TradeAsset[]>>>({});
  const [selectionMode, setSelectionMode] = useState<TradeSelectionMode>(null);
  const [pickValueAssignments, setPickValueAssignments] = useState<Record<string, PickValueBucket>>({});
  const [pickRookieAssignments, setPickRookieAssignments] = useState<Record<string, PickPlayerAssignment>>({});
  const [activeRookiePickerId, setActiveRookiePickerId] = useState<string | null>(null);

  const openTeams = useMemo(() => {
    const teamsById = new Map(importedTeams.map((team) => [team.rosterId, team]));
    return openRosterIds.map((rosterId) => teamsById.get(rosterId)).filter(Boolean) as DynastyRosterTeam[];
  }, [importedTeams, openRosterIds]);
  const singleOpenTeam = openTeams.length === 1 ? openTeams[0] : null;
  const isTwoTeamTrade = openTeams.length === 2;
  const isReceiveSelectionLocked = Boolean(selectionMode) && !isTwoTeamTrade;

  useEffect(() => {
    if (!selectionMode) return;
    if (openTeams.length < 3) {
      setSelectionMode(null);
      return;
    }
    if (!openTeams.some((team) => team.rosterId === selectionMode.teamId)) {
      setSelectionMode(null);
    }
  }, [openTeams, selectionMode]);

  const resetTradeWorkspace = useCallback(() => {
    setOpenRosterIds([]);
    setTradeSelections({});
    setSelectionMode(null);
    setPickValueAssignments({});
    setPickRookieAssignments({});
    setActiveRookiePickerId(null);
  }, []);

  function openRoster(rosterId: number) {
    if (isReceiveSelectionLocked) return;
    setOpenRosterIds((prev) => {
      if (!isNarrowViewport) {
        return prev.includes(rosterId) ? prev : [...prev, rosterId];
      }
      if (prev.includes(rosterId)) {
        return prev.filter((id) => id !== rosterId);
      }
      if (prev.length >= 2) {
        return prev;
      }
      return [...prev, rosterId];
    });
  }

  function closeRoster(rosterId: number) {
    if (isReceiveSelectionLocked) return;
    setOpenRosterIds((prev) => prev.filter((id) => id !== rosterId));
    setTradeSelections((prev) => {
      const next = { ...prev };
      delete next[rosterId];
      for (const teamId of Object.keys(next)) {
        const teamTrades = { ...next[Number(teamId)] };
        delete teamTrades[rosterId];
        next[Number(teamId)] = teamTrades;
      }
      return next;
    });
    setSelectionMode((prev) => (prev?.teamId === rosterId ? null : prev));
  }

  function isEligibleColumn(rosterId: number) {
    if (!selectionMode) return true;
    return rosterId !== selectionMode.teamId;
  }

  function getAutoTargetTeamId(sourceTeamId: number) {
    if (openTeams.length !== 2) return null;
    return openTeams.find((team) => team.rosterId !== sourceTeamId)?.rosterId ?? null;
  }

  function isAssetSelectedAnywhere(sourceTeamId: number, playerId: string) {
    return Object.values(tradeSelections).some((incomingBySource) =>
      Object.values(incomingBySource).some((assets) =>
        assets.some((asset) => asset.sourceTeamId === sourceTeamId && asset.id === playerId)
      )
    );
  }

  function isTradeRowSelected(sourceTeamId: number, playerId: string) {
    return isAssetSelectedAnywhere(sourceTeamId, playerId);
  }

  function clearPickTradeState(asset: Pick<DynastyRosterPlayer, "id" | "position">) {
    if (String(asset.position ?? "").toUpperCase() !== "PI") return;
    setPickValueAssignments((prev) => {
      if (!(asset.id in prev)) return prev;
      const next = { ...prev };
      delete next[asset.id];
      return next;
    });
    setPickRookieAssignments((prev) => {
      if (!(asset.id in prev)) return prev;
      const next = { ...prev };
      delete next[asset.id];
      return next;
    });
    setActiveRookiePickerId((prev) => (prev === asset.id ? null : prev));
  }

  function toggleTradeAsset(targetTeamId: number, sourceTeam: DynastyRosterTeam, player: DynastyRosterPlayer) {
    const asset: TradeAsset = {
      ...player,
      sourceTeamId: sourceTeam.rosterId,
      sourceTeamName: sourceTeam.displayName,
    };

    setTradeSelections((prev) => {
      const current = prev[targetTeamId] ?? {};
      const existing = current[sourceTeam.rosterId] ?? [];
      const alreadySelectedInThisBucket = existing.some(
        (entry) => entry.sourceTeamId === asset.sourceTeamId && entry.id === asset.id
      );
      const alreadySelectedElsewhere = Object.entries(prev).some(([teamId, incomingBySource]) => {
        if (Number(teamId) === targetTeamId) return false;
        return Object.values(incomingBySource).some((assets) =>
          assets.some((entry) => entry.sourceTeamId === asset.sourceTeamId && entry.id === asset.id)
        );
      });

      if (!alreadySelectedInThisBucket && alreadySelectedElsewhere) {
        return prev;
      }

      const nextAssets = alreadySelectedInThisBucket
        ? existing.filter((entry) => !(entry.sourceTeamId === asset.sourceTeamId && entry.id === asset.id))
        : [...existing, asset];

      return {
        ...prev,
        [targetTeamId]: {
          ...current,
          [sourceTeam.rosterId]: nextAssets,
        },
      };
    });

    if (isTradeRowSelected(sourceTeam.rosterId, player.id)) {
      clearPickTradeState(asset);
    }
  }

  function removeTradeAsset(targetTeamId: number, asset: TradeAsset) {
    setTradeSelections((prev) => {
      const current = prev[targetTeamId];
      if (!current) return prev;
      const existing = current[asset.sourceTeamId] ?? [];
      const nextAssets = existing.filter(
        (entry) => !(entry.sourceTeamId === asset.sourceTeamId && entry.id === asset.id)
      );
      const nextIncomingBySource = { ...current };
      if (nextAssets.length) {
        nextIncomingBySource[asset.sourceTeamId] = nextAssets;
      } else {
        delete nextIncomingBySource[asset.sourceTeamId];
      }
      const next = { ...prev };
      if (Object.keys(nextIncomingBySource).length) {
        next[targetTeamId] = nextIncomingBySource;
      } else {
        delete next[targetTeamId];
      }
      return next;
    });
    clearPickTradeState(asset);
  }

  function resolveTradeAssetValue(asset: TradeAsset | DynastyRosterPlayer) {
    if (asset.value != null) return asset.value;
    if (String(asset.position ?? "").toUpperCase() !== "PI") return asset.value;
    const assignedRookie = pickRookieAssignments[asset.id];
    if (assignedRookie) return assignedRookie.value;
    if (asset.pickSeason == null || asset.pickRound == null) return null;
    const assignedBucket = pickValueAssignments[asset.id];
    if (!assignedBucket) return null;
    const template = pickValueTemplates.get(buildBucketedPickName(asset.pickSeason, asset.pickRound, assignedBucket));
    return template?.[leagueSettings?.valueModeLabel ?? "2QB"] ?? null;
  }

  function calculateTwoTeamDisplaySummary(leftIncomingAssets: TradeAsset[], rightIncomingAssets: TradeAsset[]) {
    const comparisonSummary = calculateKtcStyleAdjustedTotals(
      leftIncomingAssets,
      rightIncomingAssets,
      resolveTradeAssetValue,
      overallMaxPlayerValue
    );
    const leftTopValue = Math.max(
      0,
      ...leftIncomingAssets.map((asset) => Math.max(0, resolveTradeAssetValue(asset) ?? asset.value ?? 0))
    );
    const rightTopValue = Math.max(
      0,
      ...rightIncomingAssets.map((asset) => Math.max(0, resolveTradeAssetValue(asset) ?? asset.value ?? 0))
    );
    const premiumSide = leftTopValue >= rightTopValue ? "left" as const : "right" as const;
    const rawWinnerSide = comparisonSummary.favoredSide === "receive" ? "left" as const : "right" as const;
    const premiumTopValue = premiumSide === "left" ? leftTopValue : rightTopValue;
    const valueAdjustment =
      premiumSide === rawWinnerSide
        ? comparisonSummary.valueAdjustment
        : Math.round(comparisonSummary.valueAdjustment * (premiumTopValue / Math.max(1, overallMaxPlayerValue)));
    const leftDisplayTotal = comparisonSummary.actualReceiveValue + (premiumSide === "left" ? valueAdjustment : 0);
    const rightDisplayTotal = comparisonSummary.actualSendValue + (premiumSide === "right" ? valueAdjustment : 0);
    const favoredSide = leftDisplayTotal >= rightDisplayTotal ? "left" as const : "right" as const;

    return {
      comparisonSummary,
      favoredSide,
      leftDisplayTotal,
      premiumSide,
      rawWinnerSide,
      rightDisplayTotal,
      valueAdjustment,
    };
  }

  function findTwoTeamAddPlayerValue(leftIncomingAssets: TradeAsset[], rightIncomingAssets: TradeAsset[]) {
    const initialSummary = calculateTwoTeamDisplaySummary(leftIncomingAssets, rightIncomingAssets);
    return Math.round(Math.abs(initialSummary.leftDisplayTotal - initialSummary.rightDisplayTotal));
  }

  function seedRecommendedTrade(recommendation: FindTradeRecommendation) {
    const myTeam = importedTeams.find((team) => team.rosterId === recommendation.sourceTeamId);
    const partnerTeam = importedTeams.find((team) => team.rosterId === recommendation.partnerTeamId);
    if (!myTeam || !partnerTeam) return;

    const toTradeAsset = (sourceTeam: DynastyRosterTeam, player: DynastyRosterPlayer): TradeAsset => ({
      ...player,
      sourceTeamId: sourceTeam.rosterId,
      sourceTeamName: sourceTeam.displayName,
    });

    setOpenRosterIds([myTeam.rosterId, partnerTeam.rosterId]);
    setSelectionMode(null);
    setIsMobileFindTradeMenuOpen(false);
    setIsTargetTeamMenuOpen(false);
    setTradeSelections({
      [myTeam.rosterId]: {
        [partnerTeam.rosterId]: recommendation.partnerAssets.map((player) => toTradeAsset(partnerTeam, player)),
      },
      [partnerTeam.rosterId]: {
        [myTeam.rosterId]: recommendation.myAssets.map((player) => toTradeAsset(myTeam, player)),
      },
    });
    if (isNarrowViewport) {
      setMobileActiveTab("trade");
    }
  }

  return {
    activeRookiePickerId,
    calculateTwoTeamDisplaySummary,
    closeRoster,
    findTwoTeamAddPlayerValue,
    getAutoTargetTeamId,
    isEligibleColumn,
    isReceiveSelectionLocked,
    isTradeRowSelected,
    isTwoTeamTrade,
    openRoster,
    openRosterIds,
    openTeams,
    pickRookieAssignments,
    pickValueAssignments,
    removeTradeAsset,
    resetTradeWorkspace,
    resolveTradeAssetValue,
    seedRecommendedTrade,
    selectionMode,
    setActiveRookiePickerId,
    setOpenRosterIds,
    setPickRookieAssignments,
    setPickValueAssignments,
    setSelectionMode,
    setTradeSelections,
    singleOpenTeam,
    toggleTradeAsset,
    tradeSelections,
    leagueSeason,
  };
}
